/* eslint-disable react/display-name */
import React, { useEffect, useState } from 'react';
import { compose } from 'react-apollo';
import { withRouter } from 'react-router';
import {
  getStaticText,
  showStaticTextIfSpecified,
  getImages,
  setLoginRedirectUrl,
  removeIsRedirectedFromPopup,
  getRuntimeEnvVariables,
} from 'helpers/Utils';
import useLoginStatusCheckPolling, { getURL, fetchStatus } from 'hooks/useLoginStatusCheck';

export const locations = {
  LOGIN: 'LOGIN',
  SIGNUP: 'SIGNUP',
  REWARDS_REGISTER: 'REWARDS_REGISTER',
  EMAIL_SIGNUP: 'EMAIL_SIGNUP',
  MFA_REGISTRATION: 'MFA_REGISTRATION',
  MFA_ACTIVATION: 'MFA_ACTIVATION',
  MFA_VERIFICATION: 'MFA_VERIFICATION',
  RESET_PASSWORD: 'RESET_PASSWORD',
  EMAIL_VERIFICATION: 'EMAIL_VERIFICATION',
  LOGGED_IN: 'LOGGED_IN',
  NEW_PASSWORD: 'NEW_PASSWORD',
  EXPIRED_LINK: 'EXPIRED_LINK',
  FORGOT_PASSWORD: 'FORGOT_PASSWORD',
  EMAIL_REGISTRATION: 'EMAIL_REGISTRATION',
  SKIP_LOGIN_OR_SIGNUP: 'SKIP_LOGIN_OR_SIGNUP',
};

const loginFlowTypes = {
  InvalidRequest: 'InvalidRequest',
  TooManyRequests: 'TooManyRequests',
  LoggedOut: 'LoggedOut',
  MfaPhoneRegistration: 'MfaPhoneRegistration',
  MfaPhoneActivate: 'MfaPhoneActivate',
  MfaPhoneVerify: 'MfaPhoneVerify',
  MfaEmailRegistration: 'MfaEmailRegistration',
  MfaEmailActivate: 'MfaEmailActivate',
  MfaEmailVerify: 'MfaEmailVerify',
  MfaTotpVerify: 'MfaTotpVerify',
  LoggedIn: 'LoggedIn',
  NewPassword: 'NewPassword',
  EmailRegistration: 'EmailRegistration',
};

const mapLoginFlowTypeToLocation = {
  MfaPhoneRegistration: 'MFA_REGISTRATION',
  MfaPhoneActivate: 'MFA_ACTIVATION',
  MfaPhoneVerify: 'MFA_VERIFICATION',
  MfaEmailRegistration: 'MFA_REGISTRATION',
  MfaEmailActivate: 'MFA_ACTIVATION',
  MfaEmailVerify: 'MFA_VERIFICATION',
  MfaTotpVerify: 'MFA_VERIFICATION',
  LoggedIn: 'LOGGED_IN',
  NewPassword: 'NEW_PASSWORD',
  EmailRegistration: 'EMAIL_REGISTRATION',
};

const loginFlowReasons = {
  REQUIRED: 'REQUIRED',
  BAD_VALUE: 'BAD_VALUE',
  TOO_MANY_REQUESTS: 'TOO_MANY_REQUESTS',
};

const loginFlowErrorsToStaticText = {
  UNKNOWN: 'GENERAL_ERROR_MESSAGE',
  USER_DOES_NOT_EXIST_ERROR: 'LOGIN_USER_DOES_NOT_EXIST_MESSAGE',
  USER_ALREADY_EXISTS: 'LOGIN_USER_ALREADY_EXISTS_MESSAGE',
  INVALID_CREDENTIALS: 'LOGIN_INVALID_CREDENTIALS_MESSAGE',
  // INVALID_OTP handling happens in Mfa Activate and Verify responses (e.g. MfaPhoneVerify)
  // For this error to happen inside InvalidRequest is quite extraordinarily.
  INVALID_OTP: 'GENERAL_ERROR_MESSAGE',
  INVALID_TOKEN: 'LOGIN_TRY_AGAIN_MESSAGE',
  INVALID_STATE: 'LOGIN_TRY_AGAIN_MESSAGE',
  NOT_AUTHORIZED: 'LOGIN_TRY_AGAIN_MESSAGE',
  ACCOUNT_LOCKED: 'TOO_MANY_ATTEMPTS_LOCKED_ACCOUNT_TEXT',
  // To end up in MFA_NOT_FOUND error, the user has to use GraphQL endpoints directly,
  // calling them in some random order.
  MFA_NOT_FOUND: 'GENERAL_ERROR_MESSAGE',
};

const socialMedia = {
  GOOGLE: 'google',
  FACEBOOK: 'facebook',
};

const DEFAULT_LOCATION = locations.SIGNUP;
const MAX_LOGIN_ATTEMPTS = 5;

const withAuth = (WrappedComponent, initialLocation) => defaultProps => {
  const {
    match: { params }, defaultLanguage, localisation, forgotPasswordPageQuery,
    createAccountMutation, loginMutation, mfaRegistrationMutation, mfaVerificationMutation,
    mfaResendMutation, mfaActivationMutation, requestPasswordResetMutation, startLocation,
    continuePasswordResetMutation, setNewPasswordMutation, emailRegistrationMutation,
    onClose, onSuccess, history, postLoginOpToken, onOpenSocialLoginFromIframe,
    onCloseSocialLoginFromIframe, onShowBannerFromIframe, onResendOTPFromIframe,
    mfaEmailRegistrationMutation,
  } = defaultProps;
  const baseURL = forgotPasswordPageQuery?.pageConfig?.header?.logo?.link?.href;
  const { location: { search } } = history;
  const defaultLocation = initialLocation || startLocation || DEFAULT_LOCATION;
  const language = params?.language || defaultLanguage;
  const searchParams = new URLSearchParams(search);

  let { staticText, images } = defaultProps;
  if (forgotPasswordPageQuery) {
    staticText = getStaticText(forgotPasswordPageQuery);
    images = getImages(forgotPasswordPageQuery);
  }

  const [emailValue, setEmailValue] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [curLoginFlow, setCurLoginFlow] = useState(loginFlowTypes.LoggedOut);
  const [currentFlow, setCurrentFlow] = useState(defaultLocation);
  const [isEmailMfaTriggeredManually, setEmailMfaTriggeredManually] = useState(false);

  /** Specifies if Graphql call is in progress */
  const [processing, setIsProcessing] = useState(false);
  /** Specifies if Graphql Resend OTP code is in progress */
  const [otpResendProcessing, setOTPResendProcessing] = useState(false);
  const [topBannerText, setTopBannerText] = useState();
  /** Specifies if Graphql call returns Error */
  const [rejected, setRejected] = useState(false);
  /** Specifies if Graphql call returns BAD_VALUE */
  const [validationError, setValidationError] = useState();
  /** Specifies if Graphql call returns TOO_MANY_REQUESTS */
  const [locked, setLockedAccount] = useState(false);
  const [location, setLocation] = useState(defaultLocation);
  const [prevLocation, setPrevLocation] = useState();
  const [loginNumberAttempts, setLoginNumberAttempts] = useState(0);
  const [socialMediaAuthWindow, setSocialMediaAuthWindow] = useState();
  const [token, setToken] = useState(searchParams.get('token'));

  const missingStaticKeys = [
    '2FA_EMAIL_VERIFICATION_OTP_HEADER',
    '2FA_EMAIL_VERIFICATION_OTP_SUBHEADER',
    '2FA_EMAIL_OTP_CODE_FIELD_LABEL',
    '2FA_EMAIL_OTP_CODE_VALIDATION',
    '2FA_EMAIL_OTP_RESEND_CODE_TEXT_LINK',
    '2FA_EMAIL_OTP_NO_CODE_TEXT',
    '2FA_VERIFICATION_BY_EMAIL_CTA',
  ].filter(key => staticText[key] === key); // value is not provided in Content Stack
  if (missingStaticKeys.length > 0) {
    console.log(`Verify by email not allowed. Reason: staticKeys '${missingStaticKeys.join(',')}' not provided`);
  }
  const isRuntimeEnable2FAEmailSet = getRuntimeEnvVariables('RUNTIME_ENABLE_2FA_EMAIL') === 'true';
  const isEmailMfaEnable = isRuntimeEnable2FAEmailSet && missingStaticKeys.length === 0;

  useEffect(() => {
    if (startLocation) {
      setLocation(startLocation);
    }
  }, [startLocation]);

  function onLocationChange(page) {
    if (page === locations.LOGIN || page === locations.SIGNUP) {
      setCurrentFlow(page);
    }
  }

  function activateEmailMfaFlow(state) {
    setEmailMfaTriggeredManually(state);
  }

  function isEmailMfaFlowActivated() {
    if (!isEmailMfaEnable) return false;
    return isEmailMfaTriggeredManually
        || curLoginFlow === loginFlowTypes.MfaEmailActivate
        || curLoginFlow === loginFlowTypes.MfaEmailVerify;
  }

  function getOtpAddressValue() {
    return isEmailMfaFlowActivated() ? emailValue : phoneNumber;
  }

  function updateOtpStaticText(srcStaticText, showEmailOption) {
    const useEmailMfa = isEmailMfaFlowActivated();
    const redefinedStaticText = {
      ...srcStaticText,
      '2FA_OTP_HEADER': useEmailMfa ? srcStaticText['2FA_EMAIL_VERIFICATION_OTP_HEADER'] : srcStaticText['2FA_PHONE_VERIFICATION_SMS_OTP_HEADER'],
      '2FA_OTP_SUBHEADER': useEmailMfa ? srcStaticText['2FA_EMAIL_VERIFICATION_OTP_SUBHEADER'] : srcStaticText['2FA_PHONE_VERIFICATION_SMS_OTP_SUBHEADER'],
      '2FA_OTP_CODE_FIELD_LABEL': useEmailMfa ? srcStaticText['2FA_EMAIL_OTP_CODE_FIELD_LABEL'] : srcStaticText['2FA_SMS_OTP_CODE_FIELD_LABEL'],
      '2FA_OTP_CODE_VALIDATION': useEmailMfa ? srcStaticText['2FA_EMAIL_OTP_CODE_VALIDATION'] : srcStaticText['2FA_SMS_OTP_CODE_VALIDATION'],
      '2FA_OTP_RESEND_CODE_TEXT_LINK': useEmailMfa ? srcStaticText['2FA_EMAIL_OTP_RESEND_CODE_TEXT_LINK'] : srcStaticText['2FA_SMS_OTP_RESEND_CODE_TEXT_LINK'],
      '2FA_OTP_NO_CODE_TEXT': useEmailMfa ? srcStaticText['2FA_EMAIL_OTP_NO_CODE_TEXT'] : srcStaticText['2FA_SMS_OTP_NO_CODE_TEXT'],
      '2FA_OTP_CTA': srcStaticText['2FA_SMS_OTP_CODE_CTA'],
    };
    // To show "Verify by Email" button:
    // 1. Feature should be enabled
    // 2. Caller should request to show this button (e.g. not show on Verification screens)
    // 3. We don't show this button on Email MFA screen (that's why it `not useEmailMfa`)
    if (isEmailMfaEnable && showEmailOption && !useEmailMfa) {
      redefinedStaticText['2FA_OTP_BY_EMAIL'] = srcStaticText['2FA_VERIFICATION_BY_EMAIL_CTA'];
    }
    return redefinedStaticText;
  }

  function updateBannerText(text) {
    if (onShowBannerFromIframe) {
      onShowBannerFromIframe({ text });
    } else {
      setTopBannerText(text);
    }
  }

  function goTo(page = defaultLocation) {
    updateBannerText(null);
    setLoginNumberAttempts(0);
    setPrevLocation(location);
    setLocation(page);
    onLocationChange(page);
  }

  function closeSocialLoginWindow() {
    if (onCloseSocialLoginFromIframe) {
      onCloseSocialLoginFromIframe();
    } else {
      socialMediaAuthWindow.close();
    }
  }

  function cancelProcessing() {
    setIsProcessing(false);
    setOTPResendProcessing(false);
  }

  function onSocialAuthSuccess({ type, phone, email }) {
    closeSocialLoginWindow();
    type && setCurLoginFlow(type);
    phone && setPhoneNumber(phone);
    email && setEmailValue(email);

    if (type === loginFlowTypes.LoggedIn && onSuccess) {
      onSuccess();
    } else {
      const newLocation = mapLoginFlowTypeToLocation[type];
      newLocation && goTo(mapLoginFlowTypeToLocation[type]);
    }
  }

  function onSocialAuthError() {
    closeSocialLoginWindow();
    updateBannerText(staticText?.GENERAL_ERROR_MESSAGE);
  }

  function onSocialAuthTimeout() {
    closeSocialLoginWindow();
  }

  function onEmailRegistration({ type }) {
    closeSocialLoginWindow();
    mapLoginFlowTypeToLocation[type]
      && goTo(mapLoginFlowTypeToLocation[type]);
  }

  const [setIsCheckStatusRunning] = useLoginStatusCheckPolling({
    initialStatus: loginFlowTypes.LoggedOut,
    EmailRegistrationStatus: loginFlowTypes.EmailRegistration,
    onSuccess: onSocialAuthSuccess,
    onEmailRegistration,
    onTimeout: onSocialAuthTimeout,
    onError: onSocialAuthError,
  });

  function cancelSocialMediaAuth() {
    socialMediaAuthWindow?.close();
    setSocialMediaAuthWindow(null);
    setIsCheckStatusRunning(false);
  }

  function resetState() {
    goTo(defaultLocation);
    setEmailValue('');
    setPhoneNumber('');
    setCurLoginFlow(loginFlowTypes.LoggedOut);
    setRejected(false);
    setEmailMfaTriggeredManually(false);
    setValidationError(false);
    setPrevLocation(null);
    cancelProcessing();
    setLoginNumberAttempts(0);
    cancelSocialMediaAuth();
  }

  function handleClose() {
    onClose();
    resetState();
  }

  function onSocialClick(socialName) {
    const url = getURL({ key: socialName, postLoginOpToken });

    if (onOpenSocialLoginFromIframe) {
      onOpenSocialLoginFromIframe(url);
    } else {
      setLoginRedirectUrl(window.top.location.href);
      const authWindow = window?.open(
        url,
        'Auth',
        'menubar=1,resizable=1,width=800,height=570'
);
      setSocialMediaAuthWindow(authWindow);
    }

    setIsCheckStatusRunning(true);
  }

  function loginExceedMaxAttempts(numberAttempts = loginNumberAttempts) {
    return location === locations.LOGIN && numberAttempts > MAX_LOGIN_ATTEMPTS - 1;
  }

  function handleReject(errorCode = null) {
    cancelProcessing();

    let key = loginFlowErrorsToStaticText[errorCode] ?? 'GENERAL_ERROR_MESSAGE';
    let bannerText = showStaticTextIfSpecified(key, staticText, staticText?.GENERAL_ERROR_MESSAGE);

    if (!loginExceedMaxAttempts(loginNumberAttempts + 1)) {
      setRejected(true);
      updateBannerText(bannerText);
    } else {
      updateBannerText(null);
    }
  }

  function handleResponse(response) {
    const loginFlowType = response?.__typename || response?.type;
    setCurLoginFlow(loginFlowType);
    setRejected(false);

    if (loginFlowType === 'TooManyRequests') {
      cancelProcessing();
      setValidationError(true);
      if (location === locations.MFA_VERIFICATION) {
        setRejected(true);
        setLockedAccount(true);
        updateBannerText(staticText?.TOO_MANY_ATTEMPTS_LOCKED_ACCOUNT_TEXT);
      }
      return;
    }

    if (response?.feedback === loginFlowReasons.BAD_VALUE) {
      cancelProcessing();
      setValidationError(true);

      if (loginFlowType === loginFlowTypes.EmailRegistration) {
        setRejected(true);
        updateBannerText(staticText?.GENERAL_ERROR_MESSAGE);
      }
      return;
    }

    switch (location) {
      case locations.EMAIL_VERIFICATION:
        return;
      case locations.RESET_PASSWORD:
        if (loginFlowType !== loginFlowTypes.InvalidRequest) {
          goTo(locations.EMAIL_VERIFICATION);
          return;
        }
        break;
      default:
        break;
    }

    setValidationError(false);
    switch (loginFlowType) {
      case loginFlowTypes.InvalidRequest:
      case loginFlowTypes.TooManyRequests:
        if (location === locations.LOGIN) {
          setLoginNumberAttempts(loginNumberAttempts + 1);
        }
        handleReject(response?.errorCode);
        return;
      case loginFlowTypes.MfaPhoneRegistration:
        response?.email && setEmailValue(response?.email);
        break;
      case loginFlowTypes.MfaPhoneActivate:
      case loginFlowTypes.MfaPhoneVerify:
        response?.phone && setPhoneNumber(response?.phone);
        break;
      case loginFlowTypes.MfaEmailRegistration:
      case loginFlowTypes.MfaEmailActivate:
      case loginFlowTypes.MfaEmailVerify:
        response?.email && setEmailValue(response?.email);
        break;
      case loginFlowTypes.NewPassword:
        response.token && setToken(response?.token);
        break;
      case loginFlowTypes.LoggedOut:
        handleClose();
        break;
      case loginFlowTypes.LoggedIn:
        if (onSuccess) { onSuccess(currentFlow); }
        if (location === locations.NEW_PASSWORD) {
          history.push({
            pathname: `${baseURL}/${staticText.DASHBOARD_ROUTE_NAME}`.replace('//', '/'),
            state: { showPasswordHasBeenChangedBanner: true },
          });
        }
        return;
      default:
        break;
    }

    const nextPage = mapLoginFlowTypeToLocation[loginFlowType];
    nextPage !== location && goTo(nextPage);
    cancelProcessing();
  }

  function onSocialPopupCallback(callback) {
    removeIsRedirectedFromPopup();
    fetchStatus({
      onSuccess: (response) => {
        handleResponse(response);
        if (callback) {
          callback();
        }
      },
      onError: (e) => { throw new Error(e); },
    });
  }

  function executeMutation(mutation, variables, responseKey, successCallback) {
    mutation({
      variables: {
        ...variables,
        language: localisation[language],
      },
    }).then(({ data }) => {
      const response = data[responseKey];
      handleResponse(response);
      successCallback && successCallback(response);

      const loginFlowType = response?.__typename || response?.type;
      if (loginFlowType === loginFlowTypes.LoggedIn && response?.redirect) {
        window.location.href = response?.redirect;
      }
    }).catch(() => {
      handleReject();
    });
  }

  function onBackClick() {
    setValidationError(false);
    setRejected(false);
    updateBannerText(null);
    setPrevLocation(null);
    setLocation(prevLocation);
    onLocationChange(prevLocation);
  }

  function handleAuthSuccess() {
    handleClose();
    onSuccess(currentFlow);
  }

  function onTopBannerDismissClick() {
    updateBannerText(null);
    setRejected(false);
  }

  function createAccount(details, successCallback) {
    setIsProcessing(true);
    executeMutation(createAccountMutation, { details, postLoginOpToken }, 'createAccount', successCallback);
  }

  function login(details, successCallback) {
    setIsProcessing(true);
    executeMutation(loginMutation, { details, postLoginOpToken }, 'login', successCallback);
  }

  function mfaResend(successCallback) {
    setOTPResendProcessing(true);
    executeMutation(mfaResendMutation, {}, 'mfaResend', successCallback);
    if (onResendOTPFromIframe) onResendOTPFromIframe();
  }

  function mfaRegistration(data, successCallback) {
    setIsProcessing(true);
    executeMutation(mfaRegistrationMutation, data, 'mfaRegistration', successCallback);
  }

  function mfaEmailRegistration(data, successCallback) {
    setIsProcessing(true);
    executeMutation(mfaEmailRegistrationMutation, data, 'mfaEmailRegistration', successCallback);
  }

  function mfaVerification(data, successCallback) {
    setIsProcessing(true);
    executeMutation(mfaVerificationMutation, data, 'mfaVerification', successCallback);
  }

  function mfaActivation(data, successCallback) {
    setIsProcessing(true);
    executeMutation(mfaActivationMutation, data, 'mfaActivation', successCallback);
  }

  function requestPasswordReset(data, successCallback) {
    setIsProcessing(true);
    executeMutation(requestPasswordResetMutation, data, 'requestPasswordReset', successCallback);
  }

  function continuePasswordReset() {
    if (!token) { goTo(locations.EXPIRED_LINK); }
    executeMutation(continuePasswordResetMutation, { token }, 'continuePasswordReset');
  }

  function setNewPassword(data, successCallback) {
    setIsProcessing(true);
    executeMutation(setNewPasswordMutation, {
      ...data,
      token,
    }, 'setNewPassword', (callbackData) => successCallback(callbackData));
  }

  function emailRegistration(data) {
    setIsProcessing(true);
    executeMutation(emailRegistrationMutation, data, 'emailRegistration');
  }

  const props = {
    ...defaultProps,
    createAccount,
    login,
    mfaRegistration,
    mfaEmailRegistration,
    emailRegistration,
    mfaResend,
    mfaVerification,
    mfaActivation,
    topBannerText,
    setTopBannerText,
    onTopBannerDismissClick,
    onBackClick,
    processing,
    otpResendProcessing,
    rejected,
    locked,
    validationError,
    staticText,
    images,
    goTo,
    prevLocation,
    location,
    locations,
    emailValue,
    phoneNumber,
    curLoginFlow,
    activateEmailMfaFlow,
    isEmailMfaFlowActivated,
    getOtpAddressValue,
    updateOtpStaticText,
    onSuccess: handleAuthSuccess,
    onClose: onClose ? handleClose : null,
    onGoogleClick: () => onSocialClick(socialMedia.GOOGLE),
    onFacebookClick: () => onSocialClick(socialMedia.FACEBOOK),
    previousAttemptIncorrect: loginExceedMaxAttempts(),
    cancelSocialMediaAuth,
    requestPasswordReset,
    continuePasswordReset,
    setNewPassword,
    onSocialPopupCallback,
  };

  if (!staticText || Object.entries(staticText).length === 0) {
    return null;
  }

  if (!images || Object.entries(images).length === 0) {
    return null;
  }

  if (forgotPasswordPageQuery && forgotPasswordPageQuery.loading) {
    return null;
  }

  return <WrappedComponent {...props} />;
};

export default compose(
  withRouter,
  withAuth,
);
